// Pinning a freshly loaded conversation to its newest message.
//
// Every message pane in the app (the /messages detail sidebar, the Messages
// drawer) fetches a thread fragment and drops it in with innerHTML. Threads
// render oldest-first, so without this the pane opens at the top — on the
// message you read weeks ago — and the current conversation is a scroll away.
//
// A single `scrollTop = scrollHeight` right after the swap is not enough: at
// that moment avatars and embedded images have no intrinsic height yet, so the
// thread keeps growing underneath the scroll that already happened and the pane
// still lands short. So we re-pin on every size change until the content
// settles or the reader scrolls for themselves — whichever comes first.

// Chronological thread scrollers opt in with data-thread-scroll; the Messages
// drawer's own fragments carry the older data-messages-thread-scroll. Panels
// that are not conversations (a calendar event's detail) carry neither and are
// left at the top, which is where their content starts.
export const THREAD_SCROLL_SELECTOR = "[data-thread-scroll], [data-messages-thread-scroll]"

// Scrolls the thread scroller in (or at) `root` to its end and keeps it there
// while late layout lands. Returns a function that stops watching early — call
// it before loading the next thread into the same pane.
export function scrollThreadToEnd(root, { settle = 1500 } = {}) {
    const scroller = findThreadScroller(root)
    if (!scroller) return () => {}

    const pin = () => { scroller.scrollTop = targetScrollTop(scroller) }
    pin()

    let stopped = false
    let observer = null
    let timer = null
    const stop = () => {
        if (stopped) return
        stopped = true
        observer?.disconnect()
        clearTimeout(timer)
        scroller.removeEventListener("wheel", stop)
        scroller.removeEventListener("touchstart", stop)
        scroller.removeEventListener("keydown", stop)
    }

    if (typeof ResizeObserver === "function") {
        observer = new ResizeObserver(() => { if (!stopped) pin() })
        // The scroller's own box is fixed by the flex layout; it's the content
        // inside it that grows as images resolve.
        observer.observe(scroller)
        Array.from(scroller.children).forEach((child) => observer.observe(child))
    }

    // Any deliberate scroll wins — never yank the reader back to the bottom.
    scroller.addEventListener("wheel", stop, { passive: true })
    scroller.addEventListener("touchstart", stop, { passive: true })
    scroller.addEventListener("keydown", stop)
    timer = setTimeout(stop, settle)

    return stop
}

// Chat bubbles are small, so the bottom of the thread *is* the newest message.
// A long-form thread (an email chain) can have a final entry taller than the
// pane, where the bottom would be the tail of the newest message rather than
// its start — those scrollers name their entries in data-thread-anchor and get
// the last one's top aligned instead, clamped so we never overscroll.
function targetScrollTop(scroller) {
    const max = Math.max(0, scroller.scrollHeight - scroller.clientHeight)
    const anchor = scroller.dataset.threadAnchor
    if (!anchor) return max

    const entries = scroller.querySelectorAll(anchor)
    const last = entries[entries.length - 1]
    if (!last) return max

    const offset = last.getBoundingClientRect().top - scroller.getBoundingClientRect().top
    return Math.min(max, Math.max(0, scroller.scrollTop + offset))
}

function findThreadScroller(root) {
    if (!root) return null
    if (root.matches?.(THREAD_SCROLL_SELECTOR)) return root
    return root.querySelector?.(THREAD_SCROLL_SELECTOR) || null
}
